import type { AssessmentProfile, AssessmentResults, AssessmentSession, LeadData } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// ─── Session ───────────────────────────────────────────────

export async function createSession(department: string = 'general'): Promise<AssessmentSession> {
  const res = await fetch(`${API_URL}/api/assessment/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ department }),
  });
  if (!res.ok) throw new Error(`Failed to create session: ${res.status}`);
  // Backend returns { session_id, department } — map to AssessmentSession shape
  const data = await res.json();
  return { id: data.session_id, department: data.department, status: 'in_progress' };
}

export async function updateSessionProfile(
  token: string,
  profileData: AssessmentProfile
): Promise<void> {
  const res = await fetch(`${API_URL}/api/assessment/session/${token}/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profile_data: profileData }),
  });
  if (!res.ok) throw new Error(`Failed to update profile: ${res.status}`);
}

export async function completeSession(
  token: string,
  profileData: AssessmentProfile
): Promise<void> {
  const res = await fetch(`${API_URL}/api/assessment/session/${token}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profile_data: profileData }),
  });
  if (!res.ok) throw new Error(`Failed to complete session: ${res.status}`);
}

// ─── Results ───────────────────────────────────────────────

/**
 * Backend returns a flat ResultsResponse; this maps it into the AssessmentSession
 * shape that ResultsPage expects (with nested results_data and contact_email).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function _mapResultsResponse(raw: any): AssessmentSession {
  const resultsReady = Boolean(raw.results_ready);

  const results_data: AssessmentResults | undefined = resultsReady
    ? {
        executive_summary: raw.executive_summary ?? '',
        overall_score: raw.overall_score ?? 0,
        scorecard: raw.scorecard ?? { areas: [] },
        process_flow: raw.process_flow ?? { nodes: [], edges: [] },
        tech_landscape: raw.tech_landscape ?? { nodes: [], edges: [] },
        recommendations: raw.recommendations ?? [],
      }
    : undefined;

  return {
    id: raw.session_id ?? '',
    department: raw.department ?? 'general',
    status: raw.status ?? 'in_progress',
    results_data,
    // ResultsPage uses contact_email presence to decide whether to show recommendations.
    // Backend signals this via the recommendations_unlocked boolean.
    contact_email: raw.recommendations_unlocked ? '__unlocked__' : undefined,
    company_name: raw.company_name ?? undefined,
  };
}

export async function getResultsPreview(token: string): Promise<AssessmentSession> {
  const res = await fetch(`${API_URL}/api/results/${token}/preview`);
  if (!res.ok) throw new Error(`Failed to fetch results preview: ${res.status}`);
  return _mapResultsResponse(await res.json());
}

export async function getResults(token: string): Promise<AssessmentSession> {
  const res = await fetch(`${API_URL}/api/results/${token}`);
  if (!res.ok) throw new Error(`Failed to fetch results: ${res.status}`);
  return _mapResultsResponse(await res.json());
}

// ─── Leads ─────────────────────────────────────────────────

export async function submitLead(
  token: string,
  data: LeadData
): Promise<{ cal_url: string; success: boolean }> {
  const res = await fetch(`${API_URL}/api/leads/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to submit lead: ${res.status}`);
  // Backend returns cal_embed_url; normalise to cal_url for EmailGate
  const body = await res.json();
  return { success: body.success, cal_url: body.cal_embed_url ?? '' };
}

// ─── Export const ──────────────────────────────────────────

export { API_URL };
